import React, { useState, useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import EmptyStateClipboard from "../../../../../assets/img/Errors/emptyDataClipboard.svg";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  Button,
  Tooltip,
} from "@fluentui/react-components";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentAvatar } from "../../../../../components/FluentUI/Fluent-Avatar";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import {
  createColumnMenu,
  formatLastUpdatedDate,
  renderlastUpdatedTooltipHeader,
  renderTooltipHeader,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks } from "../../../dashboard-util/dashboard-utils";
import {
  flattenTechMembers,
  getMemberFilteredSortedData,
  useTechMemberGroupToggle,
  getAccordionTechMemberRowRender,
  TechStafferNoDealOnFilterState,
  SeniorMembersCell,
} from "../tech-staffer-helper";
import { handleDealExport, DealExcelExports } from "./tech-staffer-deal-time-log-helper";
import "../tech-staffer-time-log.scss";

const DealTimeLogEmptyState = ({ canEdit, projectId }) => {
  const history = useHistory();

  const handleAddMembers = () => {
    history.push({
      pathname: `/projects/edit/${projectId}`,
      state: { fromDashboard: true },
    });
  };

  return (
    <div className="empty-state-container-staffer" data-testid="deal-time-log-empty-state">
      <img src={EmptyStateClipboard} alt="No Data" />
      <p className="empty-state-header">
        No Analysts or Associates added to this Deal yet
      </p>
      <p className="empty-state-subtext">
        {canEdit ? (
          <>
            There are currently no Analysts or Associates assigned to this Deal.
            <br />
            <br />
            Add Deal Team Members to begin managing Staffing and Resource allocation for this Deal.
          </>
        ) : (
          "There are currently no Analysts or Associates assigned to this Deal."
        )}
      </p>
      {canEdit && (
        <FluentButton
          text="Add Deal Team Members"
          appearance="secondaryWithOutline"
          onClick={handleAddMembers}
        />
      )}
    </div>
  );
};

DealTimeLogEmptyState.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const TechStafferDealTimeLogModal = ({ show, onHide, selectedDeal }) => {
  const user = useContext(LoginContext);
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const [memberState, setMemberState] = useState({ sort: [], filter: null });
  const { groupState, toggleGroup, setGroupState } = useTechMemberGroupToggle();
  const exportRef = useRef(null);

  const flattenedMembers = useMemo(
    () => flattenTechMembers(selectedDeal?.TechMembers || []),
    [selectedDeal]
  );

  const filteredMembers = useMemo(
    () => getMemberFilteredSortedData(flattenedMembers, memberState.filter, memberState.sort),
    [flattenedMembers, memberState.filter, memberState.sort]
  );

  const memberFlagById = useMemo(() => {
    const map = new Map();
    (selectedDeal?.Members || []).forEach((m) => {
      const id = m.User?.Id ?? m.User?.UserId;
      if (id == null) return;
      const isStafferNonOwner = m.IsStaffer && !m.IsOwner;
      const isOwnerNonStaffer = m.IsOwner && !m.IsStaffer;
      if (isStafferNonOwner || isOwnerNonStaffer) {
        map.set(id, { isStafferNonOwner, isOwnerNonStaffer });
      }
    });
    return map;
  }, [selectedDeal]);

  const summaryData = useMemo(() => {
    if (!selectedDeal) return [];
    return [{
      ProjectName: selectedDeal.ProjectName,
      LastWeekHours: selectedDeal.LastWeekHours,
      WeekendHours: selectedDeal.WeekendHours,
      TotalHours: selectedDeal.TotalHours,
      CurrentWeekHours: selectedDeal.CurrentWeekHours,
      SeniorMembersCSV: (selectedDeal.SeniorMembers || [])
        .map((m) => (typeof m === "string" ? m : m.User?.Name || m.Name) ?? "")
        .filter(Boolean)
        .join(", "),
      SeniorMembers: selectedDeal.SeniorMembers || [],
    }];
  }, [selectedDeal]);

  const summaryRef = useRef(null);

  const columnMenu = createColumnMenu(flattenedMembers);
  const isEmpty = filteredMembers.length === 0;
  const isFilterEmpty = isEmpty && (memberState.filter?.filters?.length ?? 0) > 0;
  const finalGridData = isEmpty ? [{ isEmptyRow: true }] : filteredMembers;

  const columnIsFiltered = useCallback(
    (field) => isFilteredColumn(field, memberState.filter),
    [memberState.filter]
  );

  const resetDataState = () => {
    setMemberState({ sort: [], filter: null });
    setGroupState({ Analyst: true, Associate: true });
  };

  const canReset = memberState.sort.length > 0 || !!(memberState.filter?.filters?.length);

  const AccordionRowRender = useMemo(
    () => getAccordionTechMemberRowRender(filteredMembers, groupState, toggleGroup, 6),
    [filteredMembers, groupState, toggleGroup]
  );

  useEffect(() => {
    document.body.classList.toggle("tech-dialog-open", show);
    return () => document.body.classList.remove("tech-dialog-open");
  }, [show]);

  return (
    <>
    <Dialog
      open={show}
      modalType="modal"
      onOpenChange={(_, data) => { if (!data.open) onHide(); }}
    >
      <DialogSurface aria-describedby={undefined} className="tech-staffer-modal">
        <DialogBody>
          <DialogTitle
            className="d-flex justify-content-between"
            style={{ paddingBottom: 0 }}
          >
            <div>
              {selectedDeal?.ProjectId && (
                <FluentBadge
                  shape="rounded"
                  appearance="tint"
                  size="medium"
                  color="success"
                  text={`GIB-${selectedDeal.ProjectId}`}
                />
              )}
              <div className="modal-deal-title-text">{selectedDeal?.ProjectName}</div>
              <p className="subDepartmentTitle">Deal Time Log</p>
            </div>
            <div className="d-flex align-items-start">
              <FluentTooltip
                content="Reset Filters and Sorting"
                target={
                  <FluentButton
                    appearance="secondaryWithOutline"
                    icon={
                      <FluentUiIcon
                        name="ArrowResetRegular"
                        color={canReset ? "#0F6CBD" : "#A0A0A0"}
                        size={20}
                        disabled={!canReset}
                      />
                    }
                    disabled={!canReset}
                    onClick={resetDataState}
                  />
                }
              />
              <span className="ml-2">
                <FluentTooltip
                  content="Export Data"
                  target={
                    <FluentButton
                      appearance="secondaryWithOutline"
                      icon={
                        <FluentUiIcon name="ArrowDownloadRegular" color="#0F6CBD" size={20} />
                      }
                      onClick={() => handleDealExport({ summaryRef, exportRef, selectedDeal })}
                    />
                  }
                />
              </span>
              <span className="ml-2">
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<FluentUiIcon name="DismissRegular" />}
                  />
                </DialogTrigger>
              </span>
            </div>
          </DialogTitle>

          <DialogContent>
            <div className="tech-staffer-time-log noBorder" style={{ maxWidth: "100%" }}>
              {/* Summary row */}
              <div className="summary-grid-wrapper">
                <Grid
                  data={summaryData}
                  className="tech-staffer-time-log-summary-grid"
                  resizable={true}
                  sortable={true}
                >
                  <Column
                    field="ProjectName"
                    title={renderTooltipHeader("Deal Name")}
                    columnMenu={columnMenu}
                    cell={() => (
                      <FluentTooltip
                        target={
                          <td className="summary-cell k-table-td">
                            <span>{selectedDeal?.ProjectName ?? "–"}</span>
                          </td>
                        }
                        text={selectedDeal?.ProjectName ?? ""}
                      />
                    )}
                  />
                  <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Last Wk Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.LastWeekHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Last Wknd Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.WeekendHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="TotalHours"
                    title={renderTooltipHeader("Actual Total Hrs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.TotalHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="CurrentWeekHours"
                    title={renderTooltipHeader("Current Wk Estimate")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">{props.dataItem.CurrentWeekHours ?? "–"}</td>
                    )}
                  />
                  <Column
                    field="SeniorMembers"
                    title={renderTooltipHeader("MDs/Directors/VPs")}
                    columnMenu={columnMenu}
                    cell={(props) => (
                      <td className="summary-cell k-table-td">
                        <SeniorMembersCell
                          names={(props.dataItem.SeniorMembers || []).map(
                            (m) => (typeof m === "string" ? m : m.User?.Name || m.Name)
                          ).filter(Boolean)}
                        />
                      </td>
                    )}
                  />
                </Grid>
              </div>

              <div className="detail-grid-scroll-container">
                <Grid
                  data={finalGridData}
                  dataItemKey="UserId"
                  sortable={true}
                  sort={memberState.sort}
                  filter={memberState.filter}
                  onSortChange={(e) => setMemberState((s) => ({ ...s, sort: e.sort }))}
                  onFilterChange={(e) => setMemberState((s) => ({ ...s, filter: e.filter }))}
                  rowRender={AccordionRowRender}
                  resizable={true}
                >
                  <Column
                    field="Banker"
                    title={renderTooltipHeader("Banker")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                    cell={(props) => {
                      if (props.dataItem.isEmptyRow) {
                        return (
                          <td colSpan={6} className="k-table-td">
                            {isFilterEmpty ? (
                              <TechStafferNoDealOnFilterState resetDataState={resetDataState} />
                            ) : (
                              <DealTimeLogEmptyState
                                canEdit={canEdit}
                                projectId={selectedDeal?.ProjectId}
                              />
                            )}
                          </td>
                        );
                      }
                      const memberFlags = memberFlagById.get(props.dataItem.UserId);
                      return (
                        <td className="background-white k-table-td">
                          <div className="dashboard-username" style={{ pointerEvents: "none" }}>
                            <FluentAvatar
                              size={28}
                              name={props.dataItem.Banker}
                              className="task-member-user mr-2"
                              badge={memberFlags ? { status: "blocked" } : undefined}
                            />
                            <FluentTooltip
                              target={<span style={{ marginLeft: "8px" }}>{props.dataItem.Banker}</span>}
                              text={props.dataItem.Banker ?? ""}
                            />
                            {memberFlags?.isStafferNonOwner && (
                              <Tooltip
                                content="This Staffer is a Member on this Deal, not an Owner. A Deal Owner can reassign them as an Owner to give them full Staffing permissions."
                                relationship="description"
                                withArrow
                              >
                                <span
                                  tabIndex={-1}
                                  style={{ pointerEvents: "auto" }}
                                  className="deal-team-avatar__member-info-icon"
                                >
                                  <FluentUiIcon name="InfoRegular" size={14} color="#8a8a8a" />
                                </span>
                              </Tooltip>
                            )}
                            {memberFlags?.isOwnerNonStaffer && (
                              <Tooltip
                                content={<>This User has a <strong>Banker</strong> Role, but is assigned as an Owner on this Deal. Consider making them a Member so that they can start logging their Weekly Hours and Estimates for this Deal.</>}
                                relationship="description"
                                withArrow
                              >
                                <span
                                  tabIndex={-1}
                                  style={{ pointerEvents: "auto" }}
                                  className="deal-team-avatar__member-info-icon"
                                >
                                  <FluentUiIcon name="InfoRegular" size={14} color="#8a8a8a" />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      );
                    }}
                  />
                  <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Last Wk Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          {props.dataItem.LastWeekHours ?? "–"}
                        </td>
                      )
                    }
                  />
                  <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Last Wknd Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          {props.dataItem.WeekendHours ?? "–"}
                        </td>
                      )
                    }
                  />
                  <Column
                    field="TotalHours"
                    title={renderTooltipHeader("Total Hrs")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("TotalHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          {props.dataItem.TotalHours ?? "–"}
                        </td>
                      )
                    }
                  />
                  <Column
                    field="CurrentWeekHours"
                    title={renderTooltipHeader("Current Wk Est")}
                    columnMenu={columnMenu}
                    headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td className="background-white k-table-td">
                          {props.dataItem.CurrentWeekHours ?? "–"}
                        </td>
                      )
                    }
                  />
                  <Column
                    field="LastUpdated"
                    title={renderlastUpdatedTooltipHeader("Last Updated")}
                    columnMenu={columnMenu}
                    filter="date"
                    headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                    cell={(props) =>
                      !props.dataItem.isEmptyRow && (
                        <td
                          className="background-white k-table-td"
                          style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}
                        >
                          <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                        </td>
                      )
                    }
                  />
                </Grid>
              </div>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    <DealExcelExports
      summaryData={summaryData}
      filteredMembers={filteredMembers}
      summaryRef={summaryRef}
      exportRef={exportRef}
    />
  </>
  );
};

TechStafferDealTimeLogModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedDeal: PropTypes.shape({
    ProjectId: PropTypes.number,
    ProjectName: PropTypes.string,
    TechMembers: PropTypes.array,
    Members: PropTypes.array,
    SeniorMembers: PropTypes.array,
    LastWeekHours: PropTypes.number,
    WeekendHours: PropTypes.number,
    TotalHours: PropTypes.number,
    CurrentWeekHours: PropTypes.number,
  }),
};

export default TechStafferDealTimeLogModal;
